// Copyright (c) Ping. All rights reserved.
//
// The ONE mrkdwn palette for a chat message tile, keyed by side. Consumed by
// BOTH bubble (`MessageBubble`) and compact (`StackedMessageRow`) so mention /
// link / code / quote styling can NEVER drift between the four render paths
// ({bubble,compact} × {self,other}). See the "message-tile parity" project
// memory: any tile styling change goes here so all paths inherit it.

import type { Theme } from '../design-system/theme';
import type { MrkdwnPalette } from './MrkdwnText';

/**
 * Slack-mrkdwn palette for a message tile body, keyed by whether the text sits
 * on the BRAND FILL.
 *
 * `onBrandFill` is true ONLY for a self message in BUBBLE mode (the blue-filled
 * bubble). It is false for peer bubbles AND for BOTH sides in COMPACT/stacked
 * mode (which is de-bubbled — text sits on the neutral app surface, so even a
 * self message there must use the neutral/brand palette, never white ink).
 *
 * - onBrandFill → white ink over translucent-white washes, legible on the fill.
 * - otherwise  → brand accent over a CONTRASTING wash strong enough to read as
 *   a highlighted chip. The prior `${brand}22` (13% alpha) wash looked like
 *   plain blue text over the neutral surface — the reported "mentions aren't
 *   highlighted on the other bubble" bug; raised to ~20% so it reads as a pill.
 */
export function tileMrkdwnPalette(onBrandFill: boolean, theme: Theme): MrkdwnPalette {
  if (onBrandFill) {
    return {
      link: '#FFFFFF',
      mention: '#FFFFFF',
      mentionBackground: 'rgba(255, 255, 255, 0.20)',
      code: '#FFFFFF',
      codeBackground: 'rgba(255, 255, 255, 0.18)',
      quoteBar: 'rgba(255, 255, 255, 0.5)',
    };
  }
  return {
    link: theme.colors.accent.brand,
    mention: theme.colors.accent.brand,
    mentionBackground: `${theme.colors.accent.brand}33`,
    code: theme.colors.text.primary,
    codeBackground: theme.colors.surface.secondary,
    quoteBar: theme.colors.border.strong,
  };
}
